import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { addParents } from '../../slice/parentSlice'
import { uploadFileToS3 } from '../../common/uploadFileToS3'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

// Validation Schema
const parentSchema = yup.object().shape({
    first_name: yup
        .string()
        .required('First name is required')
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name cannot exceed 50 characters')
        .matches(/^[a-zA-Z\s]+$/, 'First name can only contain letters'),
    
    last_name: yup
        .string()
        .required('Last name is required')
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name cannot exceed 50 characters')
        .matches(/^[a-zA-Z\s]+$/, 'Last name can only contain letters'),
    
    username: yup
        .string()
        .required('Username is required')
        .min(3, 'Username must be at least 3 characters')
        .max(30, 'Username cannot exceed 30 characters')
        .matches(/^[a-zA-Z0-9._-]+$/, 'Username can only contain letters, numbers, dots, hyphens, and underscores'),
    
    email: yup
        .string()
        .required('Email is required')
        .email('Please enter a valid email address'),
    
    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
    
    confirm_password: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('password')], 'Passwords must match'),
    
    country_code: yup
        .string()
        .required('Country code is required'),
    
    phone_number: yup
        .string()
        .required('Phone number is required')
        .matches(/^[0-9]{9,15}$/, 'Phone number must be between 9 and 15 digits'),
    
    dob: yup
        .date()
        .required('Date of birth is required')
        .max(new Date(), 'Date of birth cannot be in the future')
        .test('age', 'Parent must be at least 18 years old', function(value) {
            const cutoff = new Date()
            cutoff.setFullYear(cutoff.getFullYear() - 18)
            return value <= cutoff
        }),
    
    lang: yup
        .string()
        .required('Language is required')
        .oneOf(['en', 'ar'], 'Please select a valid language'),
    
    emirates_id_front: yup
        .mixed()
        .required('Emirates ID front image is required')
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return false
            return value[0].size <= 5242880 // 5MB
        })
        .test('fileType', 'Only image files are allowed (jpg, jpeg, png)', (value) => {
            if (!value || !value[0]) return false
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),
    
    emirates_id_back: yup
        .mixed()
        .required('Emirates ID back image is required')
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return false
            return value[0].size <= 5242880 // 5MB
        })
        .test('fileType', 'Only image files are allowed (jpg, jpeg, png)', (value) => {
            if (!value || !value[0]) return false
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),
})

const AddParent = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [frontPreview, setFrontPreview] = useState(null)
    const [backPreview, setBackPreview] = useState(null)
    
    // Phone state
    const [countryCode, setCountryCode] = useState('971') // Default UAE

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(parentSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            username: '',
            email: '',
            password: '',
            confirm_password: '',
            country_code: '+971',
            phone_number: '',
            dob: '',
            lang: 'en',
        }
    })

    // Watch file inputs for preview
    const emiratesFrontFile = watch('emirates_id_front')
    const emiratesBackFile = watch('emirates_id_back')

    // Update preview when file changes
    useEffect(() => {
        if (emiratesFrontFile && emiratesFrontFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setFrontPreview(reader.result)
            }
            reader.readAsDataURL(emiratesFrontFile[0])
        }
    }, [emiratesFrontFile])

    useEffect(() => {
        if (emiratesBackFile && emiratesBackFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setBackPreview(reader.result)
            }
            reader.readAsDataURL(emiratesBackFile[0])
        }
    }, [emiratesBackFile])

    const onSubmit = async (data) => {
        setIsSubmitting(true);
      
        try {
          // 1️⃣ Upload Emirates ID front
          const frontUpload = await uploadFileToS3(
            data.emirates_id_front[0],
            "parent/emirates"
          );
      
          // 2️⃣ Upload Emirates ID back
          const backUpload = await uploadFileToS3(
            data.emirates_id_back[0],
            "parent/emirates"
          );
      
          // 3️⃣ Build JSON payload (NO FormData)
          const payload = {
            first_name: data.first_name,
            last_name: data.last_name,
            username: data.username,
            email: data.email,
            password: data.password,
            country_code: data.country_code,
            phone_number: data.phone_number,
            dob: data.dob,
            lang: data.lang,
      
            // ✅ filenames only
            emirates_id_front: frontUpload.fileName,
            emirates_id_back: backUpload.fileName,
          };
      
          // 4️⃣ Dispatch Redux action
          const response = await dispatch(addParents(payload));
      
          if (response?.payload?.code === 200) {
            Swal.fire({
              icon: 'success',
              title: 'Success!',
              text: response?.payload?.message,
              timer: 2000,
              showConfirmButton: false,
            });
      
            reset();
            setTimeout(() => navigate('/parents'), 2000);
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: response?.payload?.message || 'Failed to create parent',
            });
          }
      
        } catch (error) {
          console.error(error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error?.message || 'Something went wrong',
          });
        } finally {
          setIsSubmitting(false);
        }
      };

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add Parent</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/parents')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    {/* Personal Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Personal Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* First Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                                            placeholder="Enter first name"
                                            {...register('first_name')}
                                        />
                                        {errors.first_name && (
                                            <div className="invalid-feedback">{errors.first_name.message}</div>
                                        )}
                                    </div>

                                    {/* Last Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                                            placeholder="Enter last name"
                                            {...register('last_name')}
                                        />
                                        {errors.last_name && (
                                            <div className="invalid-feedback">{errors.last_name.message}</div>
                                        )}
                                    </div>

                                    {/* Username */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Username <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                            placeholder="Enter username"
                                            {...register('username')}
                                        />
                                        {errors.username && (
                                            <div className="invalid-feedback">{errors.username.message}</div>
                                        )}
                                    </div>

                                    {/* Date of Birth */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Date of Birth <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                                            {...register('dob')}
                                        />
                                        {errors.dob && (
                                            <div className="invalid-feedback">{errors.dob.message}</div>
                                        )}
                                    </div>

                                    {/* Language */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Language <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.lang ? 'is-invalid' : ''}`}
                                            {...register('lang')}
                                        >
                                            <option value="">Select language</option>
                                            <option value="en">English</option>
                                            <option value="ar">Arabic</option>
                                        </select>
                                        {errors.lang && (
                                            <div className="invalid-feedback">{errors.lang.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Contact Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Email */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            placeholder="Enter email address"
                                            {...register('email')}
                                        />
                                        {errors.email && (
                                            <div className="invalid-feedback">{errors.email.message}</div>
                                        )}
                                    </div>

                                    {/* Phone Number with Country Code */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Phone Number <span className="text-danger">*</span>
                                        </label>
                                        <div className="d-flex gap-2">
                                            {/* Country Code Selector */}
                                            <div style={{ width: '150px' }}>
                                                <Controller
                                                    name="country_code"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <PhoneInput
                                                            country={'ae'} // Default to UAE
                                                            value={countryCode}
                                                            onChange={(phone, data) => {
                                                                setCountryCode(data.dialCode)
                                                                field.onChange(`+${data.dialCode}`)
                                                            }}
                                                            inputProps={{
                                                                name: 'country_code',
                                                                required: true,
                                                                disabled: true, // Disable typing
                                                            }}
                                                            containerStyle={{ width: '100%', height: '38px' }}
                                                            inputStyle={{ 
                                                                width: '100%', 
                                                                height: '38px',
                                                                fontSize: '14px',
                                                                borderColor: errors.country_code ? '#dc3545' : '#dee2e6',
                                                                cursor: 'pointer' // Show pointer cursor
                                                            }}
                                                            buttonStyle={{
                                                                borderColor: errors.country_code ? '#dc3545' : '#dee2e6'
                                                            }}
                                                            disableSearchIcon={false}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Phone Number Input */}
                                            <div className="flex-grow-1">
                                                <input
                                                    type="tel"
                                                    className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`}
                                                    placeholder="Enter phone number"
                                                    {...register('phone_number')}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value.replace(/\D/g, '')
                                                        if (inputValue.length <= 15) {
                                                            setValue('phone_number', inputValue)
                                                        }
                                                    }}
                                                    maxLength={15}
                                                    style={{ height: '38px' }}
                                                />
                                                {errors.phone_number && (
                                                    <div className="invalid-feedback">{errors.phone_number.message}</div>
                                                )}
                                            </div>
                                        </div>
                                        {errors.country_code && (
                                            <small className="text-danger d-block mt-1">{errors.country_code.message}</small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Security */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Account Security
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Password */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Password <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                            placeholder="Enter password"
                                            {...register('password')}
                                        />
                                        {errors.password && (
                                            <div className="invalid-feedback">{errors.password.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Password must contain uppercase, lowercase, number, and special character
                                        </small>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Confirm Password <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                            placeholder="Confirm password"
                                            {...register('confirm_password')}
                                        />
                                        {errors.confirm_password && (
                                            <div className="invalid-feedback">{errors.confirm_password.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Emirates ID Documents */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Emirates ID Documents
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Front Image */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirates ID Front <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.emirates_id_front ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('emirates_id_front')}
                                        />
                                        {errors.emirates_id_front && (
                                            <div className="invalid-feedback">{errors.emirates_id_front.message}</div>
                                        )}
                                        {frontPreview && (
                                            <div className="mt-3">
                                                <img
                                                    src={frontPreview}
                                                    alt="Front Preview"
                                                    style={{
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Back Image */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirates ID Back <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.emirates_id_back ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('emirates_id_back')}
                                        />
                                        {errors.emirates_id_back && (
                                            <div className="invalid-feedback">{errors.emirates_id_back.message}</div>
                                        )}
                                        {backPreview && (
                                            <div className="mt-3">
                                                <img
                                                    src={backPreview}
                                                    alt="Back Preview"
                                                    style={{
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/parents')}
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px',
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Create Parent'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddParent